// A workspace's documents put together for cloud's host (main.tsx):
// - the Workspace section Documents (40, the space feature `document`): ui-core's
//   document screens mounted at /<slug>/documents (`documentsRoutes`) beside this
//   part's list, with this part's way of making documents (in a space) and the
//   space's items of a document;
// - the Me part: Me's events of documents read the workspaces' lists again
//   (they come through Me's stream: `page.*` and `page.moved`), and "Move to a
//   workspace" in the "…" of one's own document;
// - `placeBase`: where a shared document opens, from a reference's path, for
//   ui-core's Document part (favorites, chips).
//
// Promise: one store of the list's state per call (per app), never a module's.
import { type QueryClient, useQuery } from "@tanstack/react-query";
import { createRoute, Outlet } from "@tanstack/react-router";
import {
  WORKSPACE_DATASET_REF_PATHS,
  workspaceDatasetPath,
} from "@trameria/cloud-api/workspace-datasets-shared";
import { m } from "@trameria/cloud-i18n";
import {
  type DocumentsHome,
  type DocumentsMount,
  type DocumentsScreenOptions,
  type DocumentsWrite,
  defineScreenPart,
  documentsRoutes,
  iconOf,
  type LiveTransport,
  lazyScreen,
  ME_DATASET_REF_PATHS,
  meDatasetPath,
  type PageDTO,
  type ScreenPart,
  useApi,
} from "@trameria/ui-core";
import { useState } from "react";
import { invalidateOverviews } from "../conversations/queries";
import {
  defineWorkspaceSection,
  useWorkspace,
  type WorkspaceSection,
} from "../workspace";
import { invalidateWsDatasets } from "../workspace-datasets/queries";
import { workspaceDatasetsRoutes } from "../workspace-datasets/routes";
import { DocumentSpace, useWorkspaceDocumentMenu } from "./document";
import {
  invalidateWsDocuments,
  invalidateWsLists,
  isWsListKey,
  useCreateWsDocument,
  useCreateWsIndex,
  wsCandidatesQuery,
} from "./queries";
import { releaseAction } from "./release";
import { createDocsStore, DocsStoreContext } from "./screen-state";
import { useNewDocumentSpace } from "./spaces";

// Loaded with the documents' route when first opened (ui-core's app/lazy.tsx).
const WsDocumentList = lazyScreen(() => import("./list"), "WsDocumentList");

/** Where a shared document is listed, from a reference's path (`{ workspace }`). */
export const workspaceDocumentsBase = (
  path: Readonly<Record<string, unknown>>,
): string | null =>
  typeof path.workspace === "string" ? `/${path.workspace}/documents` : null;

/** A write that fails at once (no space to make it in). */
class NoSpace extends Error {}

/** Makes `make(spaceId, title)` in the space a new document goes to. */
function useInSpace<R>(
  write: {
    mutate(
      input: { spaceId: string; title: string },
      options: { onSuccess(r: R): unknown; onError?(e: Error): unknown },
    ): void;
    isPending: boolean;
    isError: boolean;
    error: Error | null;
  },
  noSpace: string,
): DocumentsWrite<{ title: string }, R> {
  const target = useNewDocumentSpace();
  const [refused, setRefused] = useState<Error | null>(null);
  return {
    isPending: write.isPending,
    isError: refused !== null || write.isError,
    error: refused ?? write.error,
    mutate(input, options) {
      const space = target.space;
      if (!space) {
        const error = new NoSpace(noSpace);
        setRefused(error);
        options?.onError?.(error);
        return;
      }
      setRefused(null);
      write.mutate(
        { spaceId: space.id, title: input.title },
        {
          onSuccess: (r) => {
            target.made(space.id);
            return options?.onSuccess?.(r);
          },
          onError: options?.onError,
        },
      );
    },
  };
}

function useCreateDocumentHere(): DocumentsWrite<
  { title: string },
  { page: PageDTO }
> {
  const { slug } = useWorkspace();
  return useInSpace(useCreateWsDocument(slug), m.wd_no_space());
}

function useCreateIndexHere(): DocumentsWrite<{ title: string }, PageDTO> {
  const { slug } = useWorkspace();
  return useInSpace(useCreateWsIndex(slug), m.wd_no_space());
}

function useCandidatesHere(query: string) {
  const { slug } = useWorkspace();
  return useQuery(wsCandidatesQuery(useApi(), slug, query));
}

/** How a workspace's list makes documents and what its documents add. */
const WORKSPACE_HOME: DocumentsHome = {
  useCreateDocument: useCreateDocumentHere,
  useCreateIndex: useCreateIndexHere,
  useCandidates: useCandidatesHere,
  HeaderStart: DocumentSpace,
  useDocumentMenu: useWorkspaceDocumentMenu,
};

const WORKSPACE_MOUNT: DocumentsMount = {
  path: "documents",
  base: (params) => `/${params.slug}/documents`,
  List: WsDocumentList,
  listLabel: () => m.wd_nav(),
  home: WORKSPACE_HOME,
  // Shared documents; one's own opens in Me.
  holds: (page) => page.ownerUserId === null,
};

export interface WorkspaceDocumentsScreen {
  section: WorkspaceSection;
  /** For `createMeApp({ parts })`. */
  mePart: ScreenPart;
}

export function workspaceDocumentsScreen(options: {
  live: LiveTransport;
  blocks?: DocumentsScreenOptions["blocks"];
}): WorkspaceDocumentsScreen {
  const store = createDocsStore();
  const screens: DocumentsScreenOptions = {
    live: options.live,
    blocks: options.blocks,
    placeBase: workspaceDocumentsBase,
  };

  const section = defineWorkspaceSection({
    id: "documents",
    order: 40,
    label: () => m.wd_nav(),
    icon: iconOf("documents"),
    to: "/documents",
    feature: "document",
    routes: (ws) => {
      // The list's state for every screen under it (list, header, menu).
      const scope = createRoute({
        getParentRoute: () => ws,
        id: "documents-scope",
        component: function DocumentsScope() {
          return (
            <DocsStoreContext.Provider value={store}>
              <Outlet />
            </DocsStoreContext.Provider>
          );
        },
      });
      return [
        scope.addChildren([
          ...documentsRoutes(scope, WORKSPACE_MOUNT, screens),
          // Datasets go with the documents (their list opens from its header).
          ...workspaceDatasetsRoutes(scope),
        ]),
      ];
    },
  });

  const lists = (_p: unknown, { qc }: { qc: QueryClient }) =>
    void invalidateWsLists(qc);
  const named = (_p: unknown, { qc }: { qc: QueryClient }) => {
    void invalidateWsLists(qc);
    // A pinned document shows its name in its space's overview.
    void invalidateOverviews(qc);
  };

  const mePart = defineScreenPart({
    id: "workspace-documents",
    realtime: {
      "page.created": lists,
      "page.revision": lists,
      "page.renamed": named,
      "page.archived": named,
      "page.unarchived": named,
      "page.deleted": named,
      "page.released": lists,
      // To another space: its place, the lists and the pins.
      "page.moved": (_p, { qc }) => {
        void invalidateWsDocuments(qc);
        void invalidateOverviews(qc);
      },
      // A dataset's rows (counts) or settings: its row in the lists.
      "dataset.advanced": (_p, { qc }) => void invalidateWsDatasets(qc),
      "dataset.changed": (_p, { qc }) => void invalidateWsDatasets(qc),
    },
    // A reference to a workspace's dataset opens its screen.
    favoriteKinds: {
      dataset: {
        icon: iconOf("dataset"),
        to: (id, path) =>
          typeof path?.workspace === "string"
            ? workspaceDatasetPath(path.workspace, id)
            : meDatasetPath(id),
        refPaths: [...WORKSPACE_DATASET_REF_PATHS, ...ME_DATASET_REF_PATHS],
      },
    },
    headerActions: { page: [releaseAction] },
    // What draws a workspace's documents at once after a reload.
    persist: isWsListKey,
  });

  return { section, mePart };
}
